import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, AlertTriangle, Check, Copy, Key, Plus, Power, Send, Trash2, Webhook } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { StatusPill } from '@/components/StatusPill';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection } from '@/components/settings/SettingsPage';
import {
  developerService, ApiKeySummary, WebhookSubscription, CreateApiKeyResult, CreateWebhookResult,
} from '@/services/developer.service';

const ICON_BTN = 'flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors';
const REVEAL = 'md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100';

function relativeTime(ts?: string) {
  if (!ts) return null;
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
}

function EmptyBlock({ icon, title, text, action }: { icon: React.ReactNode; title: string; text: string; action: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft">{icon}</div>
      <div className="text-sm font-medium text-text-primary">{title}</div>
      <p className="max-w-sm text-xs text-text-muted">{text}</p>
      {action}
    </div>
  );
}

/**
 * API keys + outbound webhooks. Plaintext secrets (API keys, webhook secrets)
 * are revealed exactly once at creation, so the modal that shows them is the
 * only chance to copy.
 */
export default function DeveloperSettings() {
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [keysFailed, setKeysFailed] = useState(false);
  const [scopes, setScopes] = useState<string[]>([]);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResult | null>(null);

  const [hooks, setHooks] = useState<WebhookSubscription[] | null>(null);
  const [hooksFailed, setHooksFailed] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [hookModalOpen, setHookModalOpen] = useState(false);
  const [createdHook, setCreatedHook] = useState<CreateWebhookResult | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const refreshKeys = () => {
    setKeysFailed(false);
    return developerService.listApiKeys().then(setKeys).catch(() => setKeysFailed(true));
  };
  const refreshHooks = () => {
    setHooksFailed(false);
    return developerService.listWebhooks().then(setHooks).catch(() => setHooksFailed(true));
  };

  useEffect(() => {
    refreshKeys();
    developerService.listScopes().then((r) => setScopes(r.scopes)).catch(() => setScopes([]));
    refreshHooks();
    developerService.listEvents().then((r) => setEvents(r.events)).catch(() => setEvents([]));
  }, []);

  const revokeKey = async (k: ApiKeySummary) => {
    if (!window.confirm(`Revoke "${k.name}"? Any integration using it will start getting 401s immediately.`)) return;
    try {
      await developerService.revokeApiKey(k.apiKeyUid);
      toast.success('Key revoked');
      refreshKeys();
    } catch { /* the axios interceptor already toasted */ }
  };

  const testHook = async (h: WebhookSubscription) => {
    setTesting(h.subscriptionUid);
    try {
      const r = await developerService.testWebhook(h.subscriptionUid);
      if (r.delivered) toast.success(`Test delivered · HTTP ${r.status}`);
      else toast.error(`Test failed · ${r.error ?? 'no response'}`);
      refreshHooks();
    } catch { /* the axios interceptor already toasted */ } finally { setTesting(null); }
  };

  const toggleHook = async (h: WebhookSubscription) => {
    try {
      await developerService.updateWebhook(h.subscriptionUid, { isActive: !h.isActive });
      toast.success(h.isActive ? 'Webhook paused' : 'Webhook activated');
      refreshHooks();
    } catch { /* the axios interceptor already toasted */ }
  };

  const removeHook = async (h: WebhookSubscription) => {
    if (!window.confirm(`Delete "${h.name}"? No more events will dispatch to ${h.url}.`)) return;
    try {
      await developerService.deleteWebhook(h.subscriptionUid);
      toast.success('Webhook deleted');
      refreshHooks();
    } catch { /* the axios interceptor already toasted */ }
  };

  return (
    <>
      <SettingsPage
        eyebrow="Channels"
        title="Developer API"
        description="API keys for your own systems, and webhooks that tell them when something happens in your CRM."
        actions={(
          <>
            <Button variant="secondary" iconLeft={<Plus size={14} />} onClick={() => setHookModalOpen(true)}>New webhook</Button>
            <Button iconLeft={<Plus size={14} />} onClick={() => setKeyModalOpen(true)}>New API key</Button>
          </>
        )}
      >
        <SettingsSection
          title="API keys"
          description={(
            <>
              Call the public REST API at <code className="rounded bg-surface-alt px-1 font-mono">/api/v1/public/v1</code> from
              your own scripts. Send <code className="break-all font-mono">Authorization: Bearer pk_live_…</code> with
              each request, and give every key only the scopes it needs.
            </>
          )}
        >
          <SettingsCard padded={false}>
            {keys === null && keysFailed ? (
              <LoadErrorState bare what="your API keys" onRetry={() => void refreshKeys()} />
            ) : keys === null ? (
              <div className="space-y-2 p-5">{[0, 1].map((i) => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
            ) : keys.length === 0 ? (
              <EmptyBlock
                icon={<Key size={20} />}
                title="No API keys yet"
                text="Create one to start scripting against your CRM."
                action={<Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => setKeyModalOpen(true)}>Create the first key</Button>}
              />
            ) : (
              <ul className="divide-y divide-border">
                {keys.map((k) => <ApiKeyRow key={k.apiKeyUid} k={k} onRevoke={() => revokeKey(k)} />)}
              </ul>
            )}
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Webhooks"
          description={(
            <>
              We send a signed JSON POST to your public HTTPS URL when an event you picked happens. Check
              the <code className="rounded bg-surface-alt px-1 font-mono">X-Slightor-Signature</code> header
              with HMAC-SHA256 and your signing secret. The same signature is also sent under an older header
              name, for integrations that already use it.
            </>
          )}
        >
          <SettingsCard padded={false}>
            {hooks === null && hooksFailed ? (
              <LoadErrorState bare what="your webhooks" onRetry={() => void refreshHooks()} />
            ) : hooks === null ? (
              <div className="space-y-2 p-5">{[0, 1].map((i) => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
            ) : hooks.length === 0 ? (
              <EmptyBlock
                icon={<Webhook size={20} />}
                title="No webhooks yet"
                text="Add one to get a signed POST on your server each time something happens in your CRM."
                action={<Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => setHookModalOpen(true)}>Add the first webhook</Button>}
              />
            ) : (
              <ul className="divide-y divide-border">
                {hooks.map((h) => (
                  <WebhookRow
                    key={h.subscriptionUid}
                    h={h}
                    testing={testing === h.subscriptionUid}
                    onTest={() => testHook(h)}
                    onToggle={() => toggleHook(h)}
                    onRemove={() => removeHook(h)}
                  />
                ))}
              </ul>
            )}
          </SettingsCard>
        </SettingsSection>
      </SettingsPage>

      {/* Outside SettingsPage: its container query context also contains `position: fixed` descendants. */}
      {keyModalOpen && (
        <CreateApiKeyModal
          scopes={scopes}
          onClose={() => setKeyModalOpen(false)}
          onCreated={(r) => { setKeyModalOpen(false); setCreatedKey(r); refreshKeys(); }}
        />
      )}

      {createdKey && (
        <CreatedSecretModal
          title="Your new API key"
          label="API key"
          secret={createdKey.plaintextKey}
          onClose={() => setCreatedKey(null)}
          warning="Copy this key now — you cannot retrieve it again."
        />
      )}

      {hookModalOpen && (
        <CreateWebhookModal
          events={events}
          onClose={() => setHookModalOpen(false)}
          onCreated={(r) => { setHookModalOpen(false); setCreatedHook(r); refreshHooks(); }}
        />
      )}

      {createdHook && (
        <CreatedSecretModal
          title="Webhook signing secret"
          label="Signing secret"
          secret={createdHook.secret}
          onClose={() => setCreatedHook(null)}
          warning="Copy this secret now — you cannot retrieve it again. Use it to verify the X-Slightor-Signature HMAC."
        />
      )}
    </>
  );
}

// ─────────────────────────── Rows ──────────────────────────────────────────

function ApiKeyRow({ k, onRevoke }: { k: ApiKeySummary; onRevoke: () => void }) {
  const used = relativeTime(k.lastUsedAt);
  const requests = k.totalRequests ?? 0;
  return (
    <li className="group flex flex-wrap items-start gap-x-4 gap-y-2 px-5 py-4 transition-colors hover:bg-surface-alt/60">
      <div className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', k.isActive ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted')}>
        <Key size={17} />
      </div>
      <div className={clsx('min-w-0 flex-1 basis-48', !k.isActive && 'opacity-70')}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 truncate text-sm font-semibold text-text-primary" title={k.name}>{k.name}</span>
          {!k.isActive && <StatusPill status="Revoked" variant="neutral" />}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-text-muted">
          <code className="font-mono">{k.prefix}…</code>
          <span aria-hidden>·</span>
          <span>{requests.toLocaleString()} request{requests === 1 ? '' : 's'}</span>
          <span aria-hidden>·</span>
          <span title={k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : undefined}>{used ? `Used ${used}` : 'Never used'}</span>
        </div>
        {k.scopes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {k.scopes.map((s) => (
              <span key={s} className="rounded-full bg-info-soft px-1.5 py-0.5 font-mono text-2xs text-info">{s}</span>
            ))}
          </div>
        )}
      </div>
      {k.isActive && (
        <button
          type="button"
          onClick={onRevoke}
          className={clsx(ICON_BTN, 'ml-auto hover:bg-danger-soft hover:text-danger', REVEAL)}
          aria-label={`Revoke ${k.name}`}
          title="Revoke"
        >
          <Trash2 size={14} />
        </button>
      )}
    </li>
  );
}

function WebhookRow({ h, testing, onTest, onToggle, onRemove }: {
  h: WebhookSubscription;
  testing: boolean;
  onTest: () => void;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const delivered = relativeTime(h.lastDeliveryAt);
  const lastOk = !!h.lastStatusCode && h.lastStatusCode >= 200 && h.lastStatusCode < 300;
  return (
    <li className="group flex flex-wrap items-start gap-x-4 gap-y-2 px-5 py-4 transition-colors hover:bg-surface-alt/60">
      <div className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', h.isActive ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted')}>
        <Webhook size={17} />
      </div>
      <div className={clsx('min-w-0 flex-1 basis-48', !h.isActive && 'opacity-70')}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 truncate text-sm font-semibold text-text-primary" title={h.name}>{h.name}</span>
          <StatusPill status={h.isActive ? 'Active' : 'Paused'} variant={h.isActive ? 'success' : 'neutral'} />
          {lastOk && (
            <span className="rounded-full bg-success-soft px-2 py-0.5 text-2xs text-success">last delivered · {h.lastStatusCode}</span>
          )}
          {h.lastError && (
            <span className="inline-flex items-center gap-1 rounded-full bg-danger-soft px-2 py-0.5 text-2xs text-danger">
              <AlertCircle size={9} /> last failed
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate font-mono text-2xs text-text-muted" title={h.url}>{h.url}</div>
        {h.lastError && <div className="mt-1 line-clamp-2 break-words text-2xs text-danger">{h.lastError}</div>}
        <div className="mt-2 flex flex-wrap gap-1">
          {h.events.map((e) => (
            <span key={e} className="rounded-full bg-info-soft px-1.5 py-0.5 font-mono text-2xs text-info">{e}</span>
          ))}
        </div>
        <div className="mt-1.5 text-2xs text-text-muted">
          {(h.totalDeliveries ?? 0).toLocaleString()} delivered · {(h.totalFailures ?? 0).toLocaleString()} failed · {delivered ? `last ${delivered}` : 'nothing sent yet'}
        </div>
      </div>
      <div className={clsx('ml-auto flex shrink-0 items-center gap-1', !testing && REVEAL)}>
        <Button size="sm" variant="secondary" loading={testing} iconLeft={<Send size={12} />} onClick={onTest}>Send test</Button>
        <button
          type="button"
          onClick={onToggle}
          className={clsx(ICON_BTN, 'hover:bg-surface-alt hover:text-text-primary')}
          aria-label={`${h.isActive ? 'Pause' : 'Activate'} ${h.name}`}
          title={h.isActive ? 'Pause' : 'Activate'}
        >
          <Power size={14} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className={clsx(ICON_BTN, 'hover:bg-danger-soft hover:text-danger')}
          aria-label={`Delete ${h.name}`}
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

// ─────────────────────────── Create modals ─────────────────────────────────

function CreateApiKeyModal({ scopes, onClose, onCreated }: {
  scopes: string[];
  onClose: () => void;
  onCreated: (r: CreateApiKeyResult) => void;
}) {
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const toggleScope = (s: string) => {
    setSelectedScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Key name is required'); return; }
    if (selectedScopes.length === 0) { toast.error('Pick at least one scope'); return; }
    setBusy(true);
    try {
      const r = await developerService.createApiKey({ name: name.trim(), scopes: selectedScopes });
      onCreated(r);
    } catch { /* axios */ } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title="New API key">
      <FormField label="Name" required subtitle="What's this key for? e.g. 'Zapier prod', 'CSV nightly importer'.">
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </FormField>
      <FormField label="Scopes" required subtitle="Pick the smallest set that the integration actually needs.">
        {scopes.length === 0 ? (
          <p className="text-xs text-text-muted">No scopes are available right now. Close this and try again.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {scopes.map((s) => {
              const active = selectedScopes.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleScope(s)}
                  aria-pressed={active}
                  className={clsx(
                    'flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                    active
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-primary hover:border-primary/40',
                  )}
                >
                  <code className="min-w-0 break-all font-mono">{s}</code>
                  {active && <Check size={13} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </FormField>
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Create key</Button>
      </div>
    </Modal>
  );
}

function CreateWebhookModal({ events, onClose, onCreated }: {
  events: string[];
  onClose: () => void;
  onCreated: (r: CreateWebhookResult) => void;
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const toggle = (e: string) => {
    setSelected((prev) => prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]);
  };

  const submit = async () => {
    if (!name.trim() || !url.trim()) { toast.error('Name + URL required'); return; }
    if (selected.length === 0) { toast.error('Subscribe to at least one event'); return; }
    setBusy(true);
    try {
      const r = await developerService.createWebhook({ name: name.trim(), url: url.trim(), events: selected });
      onCreated(r);
    } catch { /* axios */ } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title="New webhook subscription" widthClass="max-w-xl">
      <FormField label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Zapier — leads pipeline" autoFocus />
      </FormField>
      <FormField label="Endpoint URL" required subtitle="HTTPS recommended. We POST signed JSON; expect 2xx within 8s.">
        <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.zapier.com/…" />
      </FormField>
      <FormField label="Events" required>
        {events.length === 0 ? (
          <p className="text-xs text-text-muted">No events are available right now. Close this and try again.</p>
        ) : (
          <div className="grid gap-1.5 sm:grid-cols-2">
            {events.map((e) => {
              const active = selected.includes(e);
              return (
                <button
                  key={e}
                  type="button"
                  onClick={() => toggle(e)}
                  aria-pressed={active}
                  className={clsx(
                    'flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-left font-mono text-xs transition-colors',
                    active
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-primary hover:border-primary/40',
                  )}
                >
                  <span className="min-w-0 break-all">{e}</span>
                  {active && <Check size={13} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </FormField>
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Create webhook</Button>
      </div>
    </Modal>
  );
}

// ─── Shared one-time-secret modal ───────────────────────────────────────────

function CreatedSecretModal({ title, label, secret, warning, onClose }: {
  title: string;
  label: string;
  secret: string;
  warning: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Could not copy. Select the text and copy it by hand.'));
  };

  return (
    <Modal open onClose={onClose} title={title}>
      <div className="space-y-3">
        <div className="flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5 text-xs text-text-secondary">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
          <strong>{warning}</strong>
        </div>
        <FormField label={label}>
          <div className="flex items-start gap-2 rounded-xl border border-border bg-surface-alt/40 p-2">
            <code className="min-w-0 flex-1 select-all break-all py-1 pl-1 font-mono text-xs text-text-primary">{secret}</code>
            <Button size="sm" variant="secondary" onClick={copy} iconLeft={copied ? <Check size={12} /> : <Copy size={12} />}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </FormField>
        <div className="flex justify-end border-t border-border pt-4">
          <Button onClick={onClose}>I've stored it safely</Button>
        </div>
      </div>
    </Modal>
  );
}
